import { WatchQueryFetchPolicy, useLazyQuery } from '@apollo/client'
import { useEffect } from 'react'

import { GetAllOrganisationsDocument } from '@/graphql/purchase-plus/generated/getAllOrganisations.generated'
import {
  OrganisationInterface,
  OrganisationsQuerySortsPropertyEnum,
  SortDirectionEnum,
} from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import useSessionUser from '@/modules/access/hooks/useSessionUser'
import { PURCHASE_PLUS_GRAPHQL_API } from '@/modules/shared/constants'
import { useViewType } from '@/modules/shared/hooks/useViewType'
import { useWatchQueryParams } from '@/modules/shared/hooks/useWatchQueryParams'
import { checkNetworkStatus } from '@/modules/shared/utils/checkNetworkStatus'
import { extractEdges } from '@/modules/shared/utils/extractEdges'
import { ignoreSingleCharacter } from '@/modules/shared/utils/ignoreSingleCharacter'
import { onFetchMore, onNextPage, onPreviousPage } from '@/modules/shared/utils/paginationUtils'

interface UseGetAllOrganisationsProps {
  onlyPurchasers?: boolean
  onlySuppliers?: boolean
  onlyParents?: boolean
  lazyQuery?: boolean
  fetchPolicy?: WatchQueryFetchPolicy
  hideUnverifiedSuppliers?: boolean
  ignoreSearchValueFromParams?: boolean
  showAllOrganisations?: boolean
  searchValue?: string
  idEq?: number
  currentOrganisationUnitSelfAndDescendants?: boolean
}

export default function useGetAllOrganisations(args?: UseGetAllOrganisationsProps) {
  const {
    onlyPurchasers,
    onlySuppliers,
    onlyParents,
    lazyQuery,
    fetchPolicy,
    hideUnverifiedSuppliers,
    ignoreSearchValueFromParams,
    showAllOrganisations,
    searchValue: searchValueFromProps,
    idEq: idEqFromProps,
    currentOrganisationUnitSelfAndDescendants,
  } = args || {}
  const paginationResultsPerPage = 20
  const { searchValue, id, verified, legalEntity, businessNumber } = useWatchQueryParams()
  const { isUtility } = useViewType()
  const { user } = useSessionUser()

  const [fetchAllOrganisations, { data, error, networkStatus, fetchMore, refetch }] = useLazyQuery(
    GetAllOrganisationsDocument,
    {
      variables: {
        first: paginationResultsPerPage,
        filters: {
          isAPurchaser: onlyPurchasers || hideUnverifiedSuppliers ? true : undefined,
          isASupplier: onlySuppliers || undefined,
          searchText: ignoreSingleCharacter(
            searchValueFromProps || (ignoreSearchValueFromParams ? undefined : searchValue)
          ),
          idIn: [idEqFromProps || id].filter(Boolean) as number[],
          isVerified: verified || hideUnverifiedSuppliers,
          legalEntity,
          businessNumberEq: businessNumber,
          parentIdPresent: onlyParents === undefined ? undefined : !onlyParents,
          currentOrganisationUnitSelfAndDescendants,
        },
        sorts: [
          {
            direction: SortDirectionEnum.Asc,
            property: OrganisationsQuerySortsPropertyEnum.CreatedAt,
          },
        ],
        adminScope: isUtility || showAllOrganisations,
        isAdmin: user?.isAdmin,
      },
      context: { uri: PURCHASE_PLUS_GRAPHQL_API },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: fetchPolicy || 'cache-and-network',
    }
  )

  const pageInfo = data?.organisations?.pageInfo

  useEffect(() => {
    if (!lazyQuery) fetchAllOrganisations()
  }, [])

  return {
    organisations: extractEdges<OrganisationInterface>(data?.organisations),
    error,
    paginationResultsPerPage,
    loadingStates: checkNetworkStatus(networkStatus),
    onFetchMoreOrganisations: () =>
      onFetchMore({
        endCursor: pageInfo?.endCursor || null,
        fetchMore,
        paginationResultsPerPage,
      }),
    onNextPage: () =>
      onNextPage({
        endCursor: pageInfo?.endCursor || null,
        refetch,
        paginationResultsPerPage,
      }),
    onPreviousPage: () =>
      onPreviousPage({
        startCursor: pageInfo?.startCursor || null,
        refetch,
        paginationResultsPerPage,
      }),
    pageInfo,
    networkStatus,
    refetch,
    fetchAllOrganisations,
  }
}
